import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import colors from 'src/utils/colors';
import { useFormContext, FieldValues, Path, useFormState, Controller } from 'react-hook-form';
import {
  hasFieldError,
  getLabel,
  toStringArray,
  AUTOCOMPLETE_STYLES,
  getContainerBoxStyles,
  FilterTags,
  SearchInput,
  OptionType,
  OptionObject,
  formValueToUIValue
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/FilterValuesAutocomplete';
import {
  useBrandSuggestions,
  BrandSuggestion
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useBrandSuggestions';

type DynamicBrandFilterProps<T extends FieldValues = FieldValues> = {
  idBase: string;
  placeholder?: string;
  showError?: boolean;
  name: Path<T>;
  buildItem?: (label: string) => OptionObject;
};

const DynamicBrandFilter = <T extends FieldValues = FieldValues>({
  idBase,
  placeholder = 'Search brands...',
  showError = true,
  name,
  buildItem
}: DynamicBrandFilterProps<T>) => {
  const { control } = useFormContext<T>();
  const { errors } = useFormState({ control });
  const hasError = showError ? hasFieldError(errors, name) : false;

  const [searchTerm, setSearchTerm] = useState('');
  const [suggestions, setSuggestions] = useState<BrandSuggestion[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<Map<string, BrandSuggestion>>(new Map());

  // Use the brand suggestions hook
  const { mutate: fetchBrandSuggestions, data: brandData, isPending: isLoading } = useBrandSuggestions();

  // Update suggestions when brand data changes
  useEffect(() => {
    if (brandData) {
      setSuggestions(brandData);
    }
  }, [brandData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm.trim().length >= 1) {
        fetchBrandSuggestions(searchTerm.trim());
      } else {
        setSuggestions([]);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, fetchBrandSuggestions]);

  // Custom UI value to form value conversion for brand filter
  const brandUiValueToFormValue = (uiValue: string[]): OptionType[] => {
    if (!buildItem) {
      return uiValue;
    }
    return uiValue.map((label) => {
      // Try to find matching suggestion from selected brands first, then current suggestions
      const selectedBrand = Array.from(selectedBrands.values()).find((s) => s.brandName === label);
      if (selectedBrand) {
        return { id: selectedBrand.brandId, name: selectedBrand.brandName };
      }

      const matchingSuggestion = suggestions.find((s) => s.brandName === label);
      if (matchingSuggestion) {
        return { id: matchingSuggestion.brandId, name: matchingSuggestion.brandName };
      }
      return buildItem(label);
    });
  };

  // Create options from suggestions
  const searchOptions = suggestions.map(
    (suggestion): OptionObject => ({
      id: suggestion.brandId,
      name: suggestion.brandName
    })
  );

  // Use options directly for server-side filtering

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const currentUIValue = formValueToUIValue(value);
        // Convert string[] back to OptionObject[] for Autocomplete
        const currentValueAsObjects = currentUIValue.map((label) => {
          const selectedBrand = Array.from(selectedBrands.values()).find((s) => s.brandName === label);
          if (selectedBrand) {
            return { id: selectedBrand.brandId, name: selectedBrand.brandName };
          }

          const matchingSuggestion = suggestions.find((s) => s.brandName === label);
          return matchingSuggestion
            ? { id: matchingSuggestion.brandId, name: matchingSuggestion.brandName }
            : { id: label, name: label };
        });

        // Merge current selections with search options to ensure selected items are always visible
        const allOptionsMap = new Map<string, OptionObject>();

        // Add currently selected items first
        currentValueAsObjects.forEach((item) => {
          allOptionsMap.set(String(item.id), item);
        });

        // Add search options (this will not overwrite existing selected items due to Map behavior)
        searchOptions.forEach((item) => {
          allOptionsMap.set(String(item.id), item);
        });

        const allOptions = Array.from(allOptionsMap.values());

        return (
          <Box sx={{ flex: 1, minWidth: '640px', marginRight: '-4px' }}>
            <Box sx={getContainerBoxStyles(hasError)}>
              <Autocomplete
                options={allOptions}
                filterOptions={(x) => x}
                freeSolo={false}
                openOnFocus
                forcePopupIcon={false}
                multiple
                disableCloseOnSelect
                disableClearable
                loading={isLoading}
                value={currentValueAsObjects}
                inputValue={searchTerm}
                onChange={(_, newValue: OptionType[]) => {
                  const labels = toStringArray(newValue);

                  // Merge existing selectedBrands with newly selected brands
                  const newSelectedBrands = new Map(selectedBrands);

                  // Remove brands that are no longer selected
                  for (const [brandId, brand] of selectedBrands.entries()) {
                    if (!labels.includes(brand.brandName)) {
                      newSelectedBrands.delete(brandId);
                    }
                  }

                  // Add newly selected brands
                  labels.forEach((label) => {
                    // Check if this brand is already in our selectedBrands
                    const alreadySelected = Array.from(newSelectedBrands.values()).some((b) => b.brandName === label);
                    if (!alreadySelected) {
                      // Try to find it in current suggestions
                      const suggestion = suggestions.find((s) => s.brandName === label);
                      if (suggestion) {
                        newSelectedBrands.set(suggestion.brandId, suggestion);
                      }
                    }
                  });

                  setSelectedBrands(newSelectedBrands);
                  onChange(brandUiValueToFormValue(labels));
                  // Keep search term for continued searching
                }}
                onInputChange={(_, newInputValue, reason) => {
                  // Only update search term for actual input, not for selection/clear events
                  if (reason === 'input') {
                    setSearchTerm(newInputValue || '');
                  }
                }}
                getOptionLabel={(option) => getLabel(option)}
                isOptionEqualToValue={(option, value) => {
                  const optionLabel = getLabel(option);
                  const valueLabel = getLabel(value as OptionType);
                  const optionId = typeof option === 'object' ? String((option as OptionObject)?.id) : null;
                  const valueId = typeof value === 'object' ? String((value as OptionObject)?.id) : null;
                  if (optionId && valueId) return optionId === valueId;
                  return optionLabel === valueLabel;
                }}
                renderInput={(params) => <SearchInput params={params} placeholder={placeholder} />}
                renderOption={(props, option, { selected, index }) => {
                  const optionId =
                    typeof option === 'object' && option !== null && 'id' in (option as OptionObject)
                      ? String((option as OptionObject).id)
                      : undefined;
                  const label = getLabel(option);

                  const key = optionId ? `${idBase}-opt-${optionId}` : `${idBase}-opt-idx-${index}`;

                  return (
                    <Box
                      component="li"
                      {...props}
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        width: '100%'
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        sx={{ padding: '4px', '&.Mui-checked': { color: colors.darkBlue } }}
                      />
                      <Text variant="body2" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}
                      </Text>
                    </Box>
                  );
                }}
                sx={AUTOCOMPLETE_STYLES}
                renderTags={(selected) => (
                  <FilterTags
                    selected={selected}
                    idBase={idBase}
                    onChange={(labels) => {
                      // Update selectedBrands map when tags are removed
                      const newSelectedBrands = new Map(selectedBrands);

                      // Remove brands that are no longer selected
                      for (const [brandId, brand] of selectedBrands.entries()) {
                        if (!labels.includes(brand.brandName)) {
                          newSelectedBrands.delete(brandId);
                        }
                      }

                      setSelectedBrands(newSelectedBrands);
                      onChange(brandUiValueToFormValue(labels));
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        );
      }}
    />
  );
};

export default DynamicBrandFilter;
